import { Order } from '@common/components/Table/table.types'
import {
  CompanyReadSchema,
  DicomTagsProps,
  ExamSeriesDicomTagMetadataSchema,
  StudySubjectViewSchema,
  StudyTimepointSubmission,
  TimepointContainerSubmissionExamMetadata,
  TimepointContainerSubmissionStatus,
  TimepointSubmissionStatus,
} from '@common/config/api/client'
import { FormDefinitionPayload } from '@study_setup/types/form_definition'
import Uppy from '@uppy/core'
import { Control } from 'react-hook-form'

export type Series = {
  fileIds: string[]
  description: string
  tooltipText: string
  acquisitionDate: string

  seriesNumber: number
  instances: Array<string>
  modality: string
  seriesInstanceUID: string

  tagData: TagsProps[]
}

export type Study = {
  name: string
  date: string
  studyDescription: string
  series: Array<Series>
}

export type DictionaryEntry = {
  name: string
  tag: string
}

export type NonDicomFile = {
  id: string
  name?: string
  basename?: string
  acquisitionDate?: string
  convert: boolean
}

export type FileSystemItem = {
  id: string
  type: 'folder' | 'file'
  date: string
  name: string
  children?: FileSystemItem[]
  size: number | null
}

export type UploadDialogProps = {
  timepointName: string
  containerParam: ContainerParam[]
  subjectPseudoId?: string
  studyId: string
  subjectId: string
  timepointSubmissionId: string
  onClose: () => void
  onSave: (props: Record<string, unknown>) => void
}

export type ContainerType = 'DICOM' | 'NON_DICOM'

export interface ContainerParam {
  additionalComments?: string | null
  name: string
  type: ContainerType
  isOptional: boolean
  modality: string
  containerSubmissionId: string
  eFormFields?: string | null
  eFormValues?: string | null
  status?: TimepointContainerSubmissionStatus
  noFilesSubmitted: boolean
  fileTypesAllowed: string[]
  containerFileTypes: string[]
  modalityAllowed: string[]
  criteriaCheck?: string
  comments?: string
  noFileSubmissionReason?: string
  examMetadata?: TimepointContainerSubmissionExamMetadata[]
  convertToDicom?: boolean
}
export interface TimePoint {
  name: string
  id: string
  children: { name: string; id: string }[]
}

export type TimePointConfigurationProps = {
  name: string
  status?: TimepointSubmissionStatus
  timePointFormId?: string | null
  eFormFields?: string | null
  eFormValues?: string | null
  subjectData?: StudySubjectViewSchema
  containerParam: ContainerParam[]
  timePoints: TimePoint[]
  onContainerSubmissionComplete?: () => void
}

export type TimepointSubmissionCell = {
  timepoint_submission?: StudyTimepointSubmission
  isLast?: boolean
  timepoint_id?: string
}

export type TimepointSubmissionRow = {
  timepoint_submissions: TimepointSubmissionCell[]
}

export type SubjectDetailsFormValues = {
  id: string
  pseudo_id: string
  blind_code: string | null
  status: string
  eligibility_status: string
  primary_site_id: string
  secondary_site_id: string | null
  subject_form_definition_id: string | null
  selected_form: FormDefinitionPayload | null
  subject_form_submission: string | null
}

export interface DicomTagsData {
  id: number
  tag: string
  tagName: string
  status: ChangeState
  originalValue: string
  newValue: string
}

export type DicomTagsTableBodyProps = {
  rows: DicomTagsData[]
  order: Order
  orderBy: keyof DicomTagsData
  containerParam: ContainerParam[]
  dynamicValues?: Record<string, string>
}

export type ReviewDicomTagsTableBodyProps = {
  order: Order
  orderBy: keyof ExamSeriesDicomTagMetadataSchema
  formControl: Control<DicomTagsProps>
}

export type AdherenceCheckUpsertPayload = {
  rule_id: string
  series_number: string
  tag_key: string
  tag_value: string | null
  is_adherent: boolean
}

export interface CheckStepProps {
  uppy: Uppy
  containerParam: ContainerParam[]
  onCheckStepComplete: (bool: boolean) => void
  selectedSubmission?: {
    name: string
    status?: string
  }
  subjectPseudoId?: string
  setActiveStep: (step: number) => void
  /** Publishes CheckStep adherence results for UploadStep to persist on PATCH. */
  onAdherenceChecksReady?: (checksByContainer: Record<string, AdherenceCheckUpsertPayload[]>) => void
}

export interface DicomTagsDialogProps {
  studyId: string
  dicomData: Study[]
  containerParam: ContainerParam[]
  onClose: (data: CompanyReadSchema | undefined) => void
  seriesId: number
  dynamicValues?: Record<string, string>
}

export type Operator = 'between' | 'contains' | 'does_not_contain' | 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'mandatory'

export type CheckItem = {
  ruleId?: string
  tagId: string
  tagName: string
  rule: Operator
  ruleLabel: string
  targetValue: string
  actualValue: string
  status: 'failed' | 'passed'
}

export interface TagsProps {
  tagId: string
  tagName: string
  value: string
}

export interface DynamicItem {
  key: string
  value: string
}

export enum ChangeState {
  MODIFY_TAG = 'MODIFY_TAG',
  UNCHANGE_TAG = 'UNCHANGE_TAG',
  ADDED_TAG = 'ADDED_TAG',
  REMOVE_TAG = 'REMOVE_TAG',
  EMPTY_TAG = 'EMPTY_TAG',
}

export type Color = 'default' | 'error' | 'secondary' | 'warning' | 'success'

export interface LegendItemProps {
  label: string
  status: ChangeState
  color: Color
}

export type DicomDataSet = {
  [key: string]: string | number | string[] | number[] | undefined | object
}

export interface DataEntry {
  key: string
  value: string
}

export interface DataStructure {
  site_id: DataEntry
  study_id: DataEntry
  subject_id: DataEntry
  timepoint_id: DataEntry
  timepoint_name: DataEntry
}

export type SeriesStatus = 'FAILED' | 'PASSED'
